import { Bot, Check, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Player } from '@/types/turn'

interface LinhaParticipanteProps {
  participante: Player
  marcado: boolean
  posicao: number | null
  onAlternar: () => void
}

/**
 * Uma pessoa (ou NPC) na lista de quem entra na rodada.
 *
 * É um `button` com papel de caixa de seleção, não uma `div` com `onClick`: a
 * tela antiga não tinha foco visível, não dava para marcar pelo teclado e o
 * leitor de tela não anunciava nada. Altura mínima de 52px pelo alvo de toque.
 */
export function LinhaParticipante({
  participante,
  marcado,
  posicao,
  onAlternar,
}: LinhaParticipanteProps) {
  const ehNpc = Boolean(participante.isNpc)
  const offline = participante.online === false

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={marcado}
      onClick={onAlternar}
      className={cn(
        'w-full min-h-[52px] flex items-center gap-3 px-3 py-2 rounded-xl border text-left',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'motion-reduce:transition-none',
        marcado
          ? 'bg-primary/10 border-primary'
          : 'bg-card border-border hover:bg-accent',
      )}
    >
      {/* caixa de seleção */}
      <span
        aria-hidden="true"
        className={cn(
          'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center',
          marcado ? 'bg-primary border-primary' : 'border-muted-foreground/40',
        )}
      >
        {marcado && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
      </span>

      {/* avatar: usa a imagem do personagem quando existe (o campo existia e
          nunca era usado — a tela antiga desenhava um ícone genérico) */}
      <span
        className={cn(
          'flex-shrink-0 w-9 h-9 rounded-full overflow-hidden flex items-center justify-center',
          ehNpc ? 'bg-purple-500/20' : 'bg-blue-500/20',
          offline && 'opacity-50',
        )}
      >
        {participante.avatar ? (
          <img src={participante.avatar} alt="" className="w-full h-full object-cover" />
        ) : ehNpc ? (
          <Bot className="w-5 h-5 text-purple-500" />
        ) : (
          <User className="w-5 h-5 text-blue-500" />
        )}
      </span>

      <span className="flex-1 min-w-0">
        <span className={cn(
          'block text-sm font-medium truncate',
          offline ? 'text-muted-foreground' : 'text-foreground',
        )}>
          {participante.name}
        </span>
        {(participante.className || ehNpc || offline) && (
          <span className="block text-xs text-muted-foreground truncate">
            {ehNpc ? 'NPC' : offline ? 'fora da mesa' : participante.className}
          </span>
        )}
      </span>

      {/* número da vez */}
      {posicao !== null && (
        <span
          aria-label={`posição ${posicao}`}
          className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center"
        >
          <span className="text-[11px] font-bold text-primary-foreground">{posicao}</span>
        </span>
      )}
    </button>
  )
}
